import { ethers } from 'ethers';
import { readContract, writeContract, prepareWriteContract } from '@wagmi/core';
import { CHAIN_CONFIGS, ERC20_ABI, BRIDGE_ABI, DEFAULT_GAS_LIMIT, ZERO_ADDRESS, SHIDO_TOKEN_META } from './constants';
import { ChainConfig, TokenType } from '../types';

// Global provider cache with wallet-specific tracking
let cachedProviders: Map<string, ethers.providers.Web3Provider> = new Map();

const SHIDO_TOKEN_HASH = ethers.utils.keccak256(
  ethers.utils.defaultAbiCoder.encode(
    ['string', 'string', 'uint8'],
    [SHIDO_TOKEN_META.name, SHIDO_TOKEN_META.symbol, SHIDO_TOKEN_META.decimals]
  )
);

export const getWrappedShidoAddress = async (
  bridgeAddress: string,
  chainIdHex: string
): Promise<string> => {
  try {
    const chainId = parseInt(chainIdHex, 16);
    const wrapped = await readContract({
      address: bridgeAddress as `0x${string}`,
      abi: BRIDGE_ABI,
      functionName: 'wrappedTokens',
      args: [SHIDO_TOKEN_HASH],
      chainId
    });
    return (wrapped as string) || ZERO_ADDRESS;
  } catch (error) {
    console.warn('Failed to resolve wrapped SHIDO address:', error);
    return ZERO_ADDRESS;
  }
};

export const resolveShidoTokenAddress = async (fromChainConfig: ChainConfig): Promise<string> => {
  if (fromChainConfig.chainId === '0x2330') {
    return ZERO_ADDRESS;
  }

  if (fromChainConfig.SHIDO && ethers.utils.isAddress(fromChainConfig.SHIDO)) {
    return fromChainConfig.SHIDO;
  }

  return getWrappedShidoAddress(fromChainConfig.bridge, fromChainConfig.chainId);
};

// Get wallet identifier for caching
const getWalletId = (): string => {
  if (typeof window === 'undefined' || !window.ethereum) return 'unknown';
  
  // Check for specific wallet identifiers
  if (window.ethereum.isMetaMask) return 'metamask';
  if (window.ethereum.isKeplr) return 'keplr';
  if (window.ethereum.isCosmostation) return 'cosmostation';
  if (window.ethereum.isRabby) return 'rabby';
  if (window.ethereum.isCoinbaseWallet) return 'coinbase';
  
  // For mobile browsers, use a more specific identifier
  const userAgent = navigator.userAgent.toLowerCase();
  if (userAgent.includes('mobile')) {
    if (userAgent.includes('safari') && !userAgent.includes('chrome')) return 'mobile-safari';
    if (userAgent.includes('chrome')) return 'mobile-chrome';
    return 'mobile-browser';
  }
  
  // Fallback to a generic identifier
  return window.ethereum.constructor?.name || 'generic';
};

// Wait for ethereum provider to be available (mobile browsers)
const waitForEthereum = async (timeout = 3000): Promise<any> => {
  return new Promise((resolve) => {
    if (window.ethereum) {
      resolve(window.ethereum);
      return;
    }

    let attempts = 0;
    const maxAttempts = timeout / 100;
    
    const checkEthereum = () => {
      attempts++;
      if (window.ethereum) {
        resolve(window.ethereum);
      } else if (attempts < maxAttempts) {
        setTimeout(checkEthereum, 100);
      } else {
        resolve(null);
      }
    };
    
    checkEthereum();
  });
};

// Initialize provider
export const getProvider = async (_chainId: string): Promise<ethers.providers.Web3Provider | null> => {
  if (typeof window === 'undefined') {
    console.error('Window is undefined');
    return null;
  }

  try {
    // Wait for ethereum provider on mobile browsers
    const ethereum = await waitForEthereum();
    if (!ethereum) {
      console.error('No ethereum provider found after waiting');
      return null;
    }

    const walletId = getWalletId();
    
    // Return cached provider for this specific wallet if available
    if (cachedProviders.has(walletId)) {
      const cachedProvider = cachedProviders.get(walletId)!;
      // Verify the cached provider is still valid
      try {
        await cachedProvider.getNetwork();
        return cachedProvider;
      } catch (error) {
        console.warn('Cached provider is invalid, creating new one');
        cachedProviders.delete(walletId);
      }
    }

    let ethereumProvider = ethereum;

    // Special handling for mobile browsers and different wallet types
    if (window.keplr && walletId === 'keplr') {
      // For Keplr, try to get the EVM provider
      ethereumProvider = ethereum || window.keplr?.ethereum;
    } else if (window.cosmostation && walletId === 'cosmostation') {
      // For Cosmostation, try to get the EVM provider
      ethereumProvider = ethereum || window.cosmostation?.ethereum;
    } else if (Array.isArray(ethereum?.providers)) {
      // Handle multiple providers (common on mobile)
      ethereumProvider = ethereum.providers.find((p: any) => p.isMetaMask) || ethereum.providers[0] || ethereum;
    }

    if (!ethereumProvider) {
      console.error('No ethereum provider found for wallet:', walletId);
      return null;
    }

    // Create provider with additional error handling for mobile
    const provider = new ethers.providers.Web3Provider(ethereumProvider, 'any');
    
    // Test the provider to ensure it's working - with retry for mobile browsers
    try {
      await provider.getNetwork();
    } catch (networkError) {
      console.warn('Provider network test failed, retrying once:', networkError);
      // For mobile browsers, try once more after a short delay
      if (walletId.includes('mobile') || walletId === 'unknown') {
        try {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second
          await provider.getNetwork();
        } catch (retryError) {
          console.warn('Provider network test failed on retry, but continuing:', retryError);
        }
      }
    }
    
    // Cache the provider for this specific wallet
    cachedProviders.set(walletId, provider);
    return provider;
  } catch (error) {
    console.error('Error initializing provider:', error);
    return null;
  }
};

// Create provider from Wagmi's wallet client
export const getProviderFromWagmi = (walletClient: any): ethers.providers.Web3Provider | null => {
  try {
    if (!walletClient || !walletClient.transport) {
      console.error('Invalid wallet client');
      return null;
    }

    // Create provider from Wagmi's transport
    const provider = new ethers.providers.Web3Provider(walletClient.transport);
    return provider;
  } catch (error) {
    console.error('Error creating provider from Wagmi:', error);
    return null;
  }
};

// Clear provider cache (useful when switching wallets)
export const clearProviderCache = () => {
  cachedProviders.clear();
};

// Switch network
export const switchNetwork = async (chainId: string): Promise<boolean> => {
  if (!window.ethereum) return false;

  try {
    await window.ethereum.request({
      method: 'wallet_switchEthereumChain',
      params: [{ chainId }],
    });
    return true;
  } catch (switchError: any) {
    // This error code indicates that the chain has not been added to MetaMask
    if (switchError.code === 4902) {
      try {
        const chainConfig = Object.values(CHAIN_CONFIGS).find(
          (config) => config.chainId === chainId
        );
        
        if (!chainConfig) {
          throw new Error(`Chain configuration not found for chainId: ${chainId}`);
        }

        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId,
              chainName: chainConfig.name,
              nativeCurrency: chainConfig.nativeCurrency || {
                name: 'ETH',
                symbol: 'ETH',
                decimals: 18,
              },
              rpcUrls: [chainConfig.rpcUrl],
              blockExplorerUrls: chainConfig.explorerUrl ? [chainConfig.explorerUrl] : undefined,
            },
          ],
        });
        return true;
      } catch (addError) {
        console.error('Error adding chain:', addError);
        return false;
      }
    }
    console.error('Error switching chain:', switchError);
    return false;
  }
};

// Get token balance using Wagmi with fallback support
export const getTokenBalanceWagmi = async (
  tokenAddress: string,
  userAddress: string
): Promise<string> => {
  try {
    // Validate token address
    if (!tokenAddress || tokenAddress === '' || !ethers.utils.isAddress(tokenAddress)) {
      console.warn('Invalid token address:', tokenAddress);
      return '0';
    }

    // First try with Wagmi
    try {
      const balance = await readContract({
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'balanceOf',
        args: [userAddress],
      });

      const decimals = await readContract({
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'decimals',
      });

      return ethers.utils.formatUnits(balance as any, decimals as any);
    } catch (wagmiError) {
      console.warn('Wagmi contract call failed, trying fallback method:', wagmiError);
      
      // Fallback to direct provider method
      return await getTokenBalanceWithFallback(tokenAddress, userAddress);
    }
  } catch (error) {
    console.error('Error getting token balance with Wagmi:', error);
    return '0';
  }
};

// Fallback token balance function with multiple RPC attempts
export const getTokenBalanceWithFallback = async (
  tokenAddress: string,
  userAddress: string
): Promise<string> => {
  // Find the chain config for this token
  let chainConfig: any = null;
  
  for (const [key, config] of Object.entries(CHAIN_CONFIGS)) {
    if (config.SHIDO === tokenAddress) {
      chainConfig = config;
      break;
    }
  }
  
  if (!chainConfig) {
    console.error('Chain config not found for token:', tokenAddress);
    return '0';
  }

  // Create list of RPC URLs to try
  const rpcUrls = [chainConfig.rpcUrl];
  if (chainConfig.fallbackRpcUrls) {
    rpcUrls.push(...chainConfig.fallbackRpcUrls);
  }

  // Try each RPC URL until one works
  for (let i = 0; i < rpcUrls.length; i++) {
    const rpcUrl = rpcUrls[i];
    console.log(`Attempting token balance fetch with RPC ${i + 1}/${rpcUrls.length}: ${rpcUrl}`);
    
    try {
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      if (tokenAddress === ZERO_ADDRESS) {
        const balanceWei = await provider.getBalance(userAddress);
        const formatted = ethers.utils.formatEther(balanceWei);
        console.log(`Successfully fetched native balance: ${formatted} using RPC: ${rpcUrl}`);
        return formatted;
      }

      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
      
      // Set a timeout for the contract calls
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('RPC call timeout')), 10000); // 10 second timeout
      });
      
      const balancePromise = tokenContract.balanceOf(userAddress);
      const decimalsPromise = tokenContract.decimals();
      
      const [balance, decimals] = await Promise.race([
        Promise.all([balancePromise, decimalsPromise]),
        timeoutPromise
      ]) as [any, any];
      
      const formattedBalance = ethers.utils.formatUnits(balance, decimals);
      console.log(`Successfully fetched balance: ${formattedBalance} using RPC: ${rpcUrl}`);
      return formattedBalance;
    } catch (error) {
      console.warn(`RPC ${rpcUrl} failed:`, error);
      
      // If this is the last RPC URL, log the error and continue to return '0'
      if (i === rpcUrls.length - 1) {
        console.error('All RPC URLs failed for token balance fetch');
      }
    }
  }
  
  return '0';
};

// Get token balance (legacy function for compatibility)
export const getTokenBalance = async (
  tokenAddress: string,
  userAddress: string,
  provider: ethers.providers.Web3Provider
): Promise<string> => {
  try {
    if (tokenAddress === ZERO_ADDRESS) {
      const balanceWei = await provider.getBalance(userAddress);
      return ethers.utils.formatEther(balanceWei);
    }

    // Validate token address
    if (!tokenAddress || tokenAddress === '' || !ethers.utils.isAddress(tokenAddress)) {
      console.warn('Invalid token address:', tokenAddress);
      return '0';
    }

    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
    const balance = await tokenContract.balanceOf(userAddress);
    const decimals = await tokenContract.decimals();
    return ethers.utils.formatUnits(balance, decimals);
  } catch (error) {
    console.error('Error getting token balance:', error);
    return '0';
  }
};

// Check token allowance using Wagmi with fallback support
export const checkAllowanceWagmi = async (
  tokenAddress: string,
  userAddress: string,
  spenderAddress: string
): Promise<string> => {
  try {
    if (tokenAddress === ZERO_ADDRESS) {
      return '0';
    }

    // Validate token address
    if (!tokenAddress || tokenAddress === '' || !ethers.utils.isAddress(tokenAddress)) {
      console.warn('Invalid token address for allowance check:', tokenAddress);
      return '0';
    }

    // First try with Wagmi
    try {
      const allowance = await readContract({
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'allowance',
        args: [userAddress, spenderAddress],
      });

      const decimals = await readContract({
        address: tokenAddress as `0x${string}`,
        abi: ERC20_ABI,
        functionName: 'decimals',
      });

      return ethers.utils.formatUnits(allowance as any, decimals as any);
    } catch (wagmiError) {
      console.warn('Wagmi allowance check failed, trying fallback method:', wagmiError);
      
      // Fallback to direct provider method
      return await checkAllowanceWithFallback(tokenAddress, userAddress, spenderAddress);
    }
  } catch (error) {
    console.error('Error checking allowance with Wagmi:', error);
    return '0';
  }
};

// Fallback allowance check function with multiple RPC attempts
export const checkAllowanceWithFallback = async (
  tokenAddress: string,
  userAddress: string,
  spenderAddress: string
): Promise<string> => {
  if (tokenAddress === ZERO_ADDRESS) {
    return '0';
  }

  // Find the chain config for this token
  let chainConfig: any = null;
  
  for (const [key, config] of Object.entries(CHAIN_CONFIGS)) {
    if (config.SHIDO === tokenAddress) {
      chainConfig = config;
      break;
    }
  }
  
  if (!chainConfig) {
    console.error('Chain config not found for token:', tokenAddress);
    return '0';
  }

  // Create list of RPC URLs to try
  const rpcUrls = [chainConfig.rpcUrl];
  if (chainConfig.fallbackRpcUrls) {
    rpcUrls.push(...chainConfig.fallbackRpcUrls);
  }

  // Try each RPC URL until one works
  for (let i = 0; i < rpcUrls.length; i++) {
    const rpcUrl = rpcUrls[i];
    console.log(`Attempting allowance check with RPC ${i + 1}/${rpcUrls.length}: ${rpcUrl}`);
    
    try {
      const provider = new ethers.providers.JsonRpcProvider(rpcUrl);
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
      
      // Set a timeout for the contract calls
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('RPC call timeout')), 10000); // 10 second timeout
      });
      
      const allowancePromise = tokenContract.allowance(userAddress, spenderAddress);
      const decimalsPromise = tokenContract.decimals();
      
      const [allowance, decimals] = await Promise.race([
        Promise.all([allowancePromise, decimalsPromise]),
        timeoutPromise
      ]) as [any, any];
      
      const formattedAllowance = ethers.utils.formatUnits(allowance, decimals);
      console.log(`Successfully fetched allowance: ${formattedAllowance} using RPC: ${rpcUrl}`);
      return formattedAllowance;
    } catch (error) {
      console.warn(`RPC ${rpcUrl} failed for allowance check:`, error);
      
      // If this is the last RPC URL, log the error and continue to return '0'
      if (i === rpcUrls.length - 1) {
        console.error('All RPC URLs failed for allowance check');
      }
    }
  }
  
  return '0';
};

// Check token allowance (legacy function for compatibility)
export const checkAllowance = async (
  tokenAddress: string,
  userAddress: string,
  spenderAddress: string,
  provider: ethers.providers.Web3Provider
): Promise<string> => {
  try {
    if (tokenAddress === ZERO_ADDRESS) {
      return '0';
    }

    // Validate token address
    if (!tokenAddress || tokenAddress === '' || !ethers.utils.isAddress(tokenAddress)) {
      console.warn('Invalid token address for allowance check:', tokenAddress);
      return '0';
    }

    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, provider);
    const allowance = await tokenContract.allowance(userAddress, spenderAddress);
    const decimals = await tokenContract.decimals();
    return ethers.utils.formatUnits(allowance, decimals);
  } catch (error) {
    console.error('Error checking allowance:', error);
    return '0';
  }
};

// Approve token using Wagmi (works with all wallets)
export const approveTokenWagmi = async (
  tokenAddress: string,
  spenderAddress: string,
  amount: string
): Promise<string> => {
  try {
    if (tokenAddress === ZERO_ADDRESS) {
      throw new Error('Cannot approve native token');
    }

    const decimals = await readContract({
      address: tokenAddress as `0x${string}`,
      abi: ERC20_ABI,
      functionName: 'decimals',
    });

    const parsedAmount = ethers.utils.parseUnits(amount, decimals as any);

    const config = await prepareWriteContract({
      address: tokenAddress as `0x${string}`,
      abi: ERC20_ABI,
      functionName: 'approve',
      args: [spenderAddress, parsedAmount],
    });

    const { hash } = await writeContract(config);
    return hash;
  } catch (error) {
    console.error('Error approving token with Wagmi:', error);
    throw error;
  }
};

// Approve token (legacy function for compatibility)
export const approveToken = async (
  tokenAddress: string,
  spenderAddress: string,
  amount: string,
  provider: ethers.providers.Web3Provider
): Promise<string> => {
  try {
    if (tokenAddress === ZERO_ADDRESS) {
      throw new Error('Cannot approve native token');
    }

    const signer = provider.getSigner();
    const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
    const decimals = await tokenContract.decimals();
    const parsedAmount = ethers.utils.parseUnits(amount, decimals);
    
    const tx = await tokenContract.approve(spenderAddress, parsedAmount);
    await tx.wait();
    return tx.hash;
  } catch (error) {
    console.error('Error approving token:', error);
    throw error;
  }
};

// Bridge token using Wagmi (works with all wallets)
export const bridgeTokenWagmi = async (
  fromChainConfig: ChainConfig,
  toChainConfig: ChainConfig,
  tokenType: TokenType,
  amount: string,
  recipientAddress: string,
  feeData?: {
    fee: string;
    deadline: number;
    signature: string;
  }
): Promise<string> => {
  try {
    const tokenAddress = await resolveShidoTokenAddress(fromChainConfig);
    if (!tokenAddress || tokenAddress === '') {
      throw new Error(`${tokenType} not supported on ${fromChainConfig.name}`);
    }
    if (fromChainConfig.chainId !== '0x2330' && tokenAddress === ZERO_ADDRESS) {
      throw new Error('Wrapped SHIDO not found on Ethereum. Bridge from Shido first.');
    }
    
    // Get the actual token decimals from the source chain
    const decimals = 18;
    
    const parsedAmount = ethers.utils.parseUnits(amount, decimals);

    if (!feeData) {
      throw new Error('Fee data is required for cross-chain transactions');
    }
    
    // Convert fee to proper format - use the same decimals as the amount (source chain format)
    const parsedFee = ethers.utils.parseUnits(feeData.fee, decimals);
    
    // Determine direction and use appropriate function
    const isFromShido = fromChainConfig.chainId === '0x2330'; // Shido mainnet
    const isToShido = toChainConfig.chainId === '0x2330'; // Shido mainnet
    
    let config;
    if (isFromShido && !isToShido) {
      // FROM Shido TO Ethereum: use crossTo with native SHIDO
      config = await prepareWriteContract({
        address: fromChainConfig.bridge as `0x${string}`,
        abi: BRIDGE_ABI,
        functionName: 'crossTo',
        args: [
          tokenAddress,
          toChainConfig.bip44ChainId,
          recipientAddress,
          parsedAmount,
          DEFAULT_GAS_LIMIT,
          parsedFee,
          feeData.deadline,
          feeData.signature
        ],
        value: parsedAmount,
      });
    } else if (!isFromShido && isToShido) {
      // FROM Ethereum TO Shido: use crossBack with wrapped SHIDO
      config = await prepareWriteContract({
        address: fromChainConfig.bridge as `0x${string}`,
        abi: BRIDGE_ABI,
        functionName: 'crossBack',
        args: [
          tokenAddress,
          toChainConfig.bip44ChainId,
          recipientAddress,
          parsedAmount,
          DEFAULT_GAS_LIMIT,
          parsedFee,
          feeData.deadline,
          feeData.signature
        ]
      });
    } else {
      throw new Error('Invalid bridge direction. Please bridge between Shido and Ethereum.');
    }
    
    const { hash } = await writeContract(config);
    return hash;
  } catch (error) {
    console.error('Error bridging token with Wagmi:', error);
    throw error;
  }
};

// Bridge token (legacy function for compatibility)
export const bridgeToken = async (
  fromChainConfig: ChainConfig,
  toChainConfig: ChainConfig,
  tokenType: TokenType,
  amount: string,
  recipientAddress: string,
  provider: ethers.providers.Web3Provider,
  feeData?: {
    fee: string;
    deadline: number;
    signature: string;
  }
): Promise<string> => {
  try {
    const signer = provider.getSigner();
    const bridgeContract = new ethers.Contract(fromChainConfig.bridge, BRIDGE_ABI, signer);
    const tokenAddress = await resolveShidoTokenAddress(fromChainConfig);
    if (!tokenAddress || tokenAddress === '') {
      throw new Error(`${tokenType} not supported on ${fromChainConfig.name}`);
    }
    if (fromChainConfig.chainId !== '0x2330' && tokenAddress === ZERO_ADDRESS) {
      throw new Error('Wrapped SHIDO not found on Ethereum. Bridge from Shido first.');
    }
    
    // SHIDO uses 18 decimals (native and wrapped)
    const decimals = 18;
    
    const parsedAmount = ethers.utils.parseUnits(amount, decimals);
    

    if (!feeData) {
      throw new Error('Fee data is required for cross-chain transactions');
    }
    
    // Convert fee to proper format - use the same decimals as the amount (source chain format)
    const parsedFee = ethers.utils.parseUnits(feeData.fee, decimals);
    
    // Determine direction and use appropriate function
    const isFromShido = fromChainConfig.chainId === '0x2330'; // Shido mainnet
    const isToShido = toChainConfig.chainId === '0x2330'; // Shido mainnet
    
    let tx;
    if (isFromShido && !isToShido) {
      // FROM Shido TO Ethereum: use crossTo with native SHIDO
      tx = await bridgeContract.crossTo(
        tokenAddress,
        toChainConfig.bip44ChainId,
        recipientAddress,
        parsedAmount,
        DEFAULT_GAS_LIMIT,
        parsedFee,
        feeData.deadline,
        feeData.signature,
        { value: parsedAmount }
      );
    } else if (!isFromShido && isToShido) {
      // FROM Ethereum TO Shido: use crossBack with wrapped SHIDO
      tx = await bridgeContract.crossBack(
        tokenAddress,
        toChainConfig.bip44ChainId,
        recipientAddress,
        parsedAmount,
        DEFAULT_GAS_LIMIT,
        parsedFee,
        feeData.deadline,
        feeData.signature
      );
    } else {
      throw new Error('Invalid bridge direction. Please bridge between Shido and Ethereum.');
    }
    
    await tx.wait();
    return tx.hash;
  } catch (error) {
    console.error('Error bridging token:', error);
    throw error;
  }
};

// Estimate bridge fee
export const estimateBridgeFee = async (
  fromChainConfig: ChainConfig,
  toChainConfig: ChainConfig,
  provider: ethers.providers.Web3Provider
): Promise<string> => {
  try {

    // Get current gas price
    const gasPrice = await provider.getGasPrice();
    
    // Estimate gas for the transaction (typical bridge transaction uses around 150,000-200,000 gas)
    const estimatedGas = ethers.BigNumber.from(200000);
    
    // Calculate fee based on gas price and estimated gas
    const baseFee = gasPrice.mul(estimatedGas);
    
    // Add a small buffer (20%) to ensure enough funds for the transaction
    const bufferedFee = baseFee.mul(120).div(100);
    
    // Convert to ETH
    const feeInEth = ethers.utils.formatEther(bufferedFee);
    
    return feeInEth;
  } catch (error) {
    console.error('Error estimating bridge fee:', error);
    return '0.001'; // Default small fee instead of 0
  }
};

// Check cross-chain message status with destination transaction hash
export const checkCrossChainMessageStatus = async (
  txHash: string
): Promise<{
  status: 'pending' | 'processing' | 'completed' | 'failed';
  toChainTxHash?: string;
}> => {
  try {
    // Use the Bridge API to check the cross-chain message status (mainnet)
    const apiUrl = `https://bridge-api.wanchain.org/api/status/msg/${txHash}`;
    const response = await fetch(apiUrl);
    
    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }
    
    const data = await response.json();
    
    // The API returns an array of objects
    if (Array.isArray(data) && data.length > 0) {
      const txData = data[0];
      
      if (txData.status) {
        // Make sure we handle the status case-insensitively
        const status = String(txData.status).toLowerCase();
        
        // Map the API status to our status types
        if (status === 'success' || status === 'confirmed') {
          return {
            status: 'completed',
            toChainTxHash: txData.receiveTxHash
          };
        } else if (status === 'failed' || status === 'rejected' || status === 'error') {
          return { status: 'failed' };
        } else if (status === 'processing') {
          return { status: 'processing' };
        } else if (status === 'pending') {
          return { status: 'pending' };
        }
      }
    }
    
    // If we couldn't determine the status or it's still in progress
    return { status: 'pending' };
  } catch (error) {
    console.error('Error checking cross-chain message status:', error);
    // If there's an error, we assume the transaction is still pending
    return { status: 'pending' };
  }
};

// Legacy function for backward compatibility
export const checkCrossChainMessageStatusLegacy = async (
  txHash: string
): Promise<'pending' | 'processing' | 'completed' | 'failed'> => {
  const result = await checkCrossChainMessageStatus(txHash);
  return result.status;
};

// Check transaction status
export const checkTransactionStatus = async (
  txHash: string,
  chainId: string
): Promise<'pending' | 'processing' | 'completed' | 'failed'> => {
  try {
    const provider = await getProvider(chainId);
    if (!provider) throw new Error('Failed to initialize provider');
    
    const receipt = await provider.getTransactionReceipt(txHash);
    
    if (!receipt) {
      return 'pending';
    }
    
    return receipt.status ? 'completed' : 'failed';
  } catch (error) {
    console.error('Error checking transaction status:', error);
    return 'pending';
  }
};

// Format address for display
export const formatAddress = (address: string): string => {
  if (!address) return '';
  return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
};

// Get transaction URL for explorer
export const getExplorerTxUrl = (chainId: string, txHash: string): string => {
  const chainConfig = Object.values(CHAIN_CONFIGS).find(
    (config) => config.chainId === chainId
  );
  
  if (!chainConfig || !chainConfig.explorerUrl) return '';
  
  return `${chainConfig.explorerUrl}/tx/${txHash}`;
};
